import { Button, CreateButton, Drawer, ExportButton, getDefaultSortOrder, Icons, List, Space, Table, Tag, Typography, useTable } from "@pankod/refine-antd";
import { CanAccess, CrudFilters, HttpError, IResourceComponentsProps, useExport, useNavigation, useOne } from "@pankod/refine-core";
import { BackgroundScreeningFilter } from "components/backgroundscreening/backgroundscreeningfilter";
import { RenderWorkflowModal } from "components/common";
import { ShowUserCard } from "components/profile/showUserCard";
import { displayRelativeDate } from "components/utils/displayRelativeDate";
import { LookupButton } from "components/utils/lookupButton";
import { IBackgroundScreening, IBackgroundScreeningFilterVariables, IScreeningMetadata, IState } from "interfaces";
import { useEffect, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_EXPORT, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, doesExist, RESOURCE_PATH, STALE_DURATION } from "scripts/site";

export const BackgroundScreeningList: React.FC<IResourceComponentsProps> = () => {
  const { show } = useNavigation();

  function rowClick(record: IBackgroundScreening) {
    return {
      onClick: () => {
        show(RESOURCE_PATH.BACKGROUND_SCREENING, record.id);
      },
    };
  }
  // Fetch entity metadata using useOne hook
  // This hook retrieves metadata for the background screening entity
  // It uses the DATAPROVIDER_LOOKUP to get the metadata from the specified resource path
  // The id is set to RESOURCE_PATH.BACKGROUND_SCREENING, which is a constant defined
  const { data: entityMetadata, isLoading } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.BACKGROUND_SCREENING,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION
    }
  });

  const entityStates = entityMetadata?.data?.states as IState[] || []; // Assign it directly

  // State to manage filter form visibility, asset view, and data fetching
  const [filterOpen, switchFilterForm] = useState(false);
  const [fetchData, switchFetchData] = useState(false);

  // Fetch table data using useTable hook
  // This hook retrieves data for the background screening list
  // It uses the DATAPROVIDER_READ to get the data from the specified resource path
  const { tableProps, tableQueryResult, searchFormProps, sorter, filters } =
    useTable<IBackgroundScreening, HttpError, IBackgroundScreeningFilterVariables>({
      dataProviderName: DATAPROVIDER_READ,
      resource: RESOURCE_PATH.BACKGROUND_SCREENING,
      initialSorter: [
        {
          field: "updatedAt",
          order: "desc",
        },
      ],
      onSearch: (params) => {
        const filters: CrudFilters = [];
        const { q, name, tcsEmployeeId, tcsEmailId, managerUserId, countryId, locationId, won, state } = params;
        filters.push({
          field: "q",
          operator: "contains",
          value: q,
        });
        filters.push({
          field: "name",
          operator: "contains",
          value: name,
        });
        filters.push({
          field: "tcsEmployeeId",
          operator: "eq",
          value: tcsEmployeeId,
        });
        filters.push({
          field: "tcsEmailId",
          operator: "contains",
          value: tcsEmailId,
        });
        filters.push({
          field: "managerUserId",
          operator: "eq",
          value: managerUserId,
        });
        filters.push({
          field: "countryId",
          operator: "contains",
          value: countryId,
        });
        filters.push({
          field: "locationId",
          operator: "contains",
          value: locationId,
        });
        filters.push({
          field: "won",
          operator: "contains",
          value: won,
        });
        filters.push({
          field: "stateManager.state",
          operator: "eq",
          value: state,
        });

        return filters;
      },
      queryOptions: {
        enabled: fetchData,
      },
    });

  let isFiltered =
    filters &&
    filters.length > 0 &&
    filters.some((fltr) => fltr.value !== undefined);

  const clearFilters = () => {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  };
  const metaConfig = entityMetadata?.data?.config;

  // Effect to trigger data fetching when the component mounts or when certain conditions change
  useEffect(() => {
    if (!isLoading) {
      switchFetchData(true);
    }
  }, [isLoading]);
  const totalRequests = tableQueryResult?.data?.total ?? 0;

  const { triggerExport, isLoading: exportLoading } = useExport<any>({
    dataProviderName: DATAPROVIDER_EXPORT,
    resourceName: RESOURCE_PATH.BACKGROUND_SCREENING,
    pageSize: metaConfig?.export?.pageSize ?? 100,
    sorter: [
      {
        field: "updatedAt",
        order: "desc"
      }
    ],
  });

  return (
    <List
      headerProps={{
        title:
          (tableQueryResult.isLoading ? "Loading" : totalRequests) +
          " Background Screening Request" +
          (totalRequests !== 1 ? "s" : ""),
        tags: (
          <>
            <Button
              key="filter"
              type="default"
              icon={
                isFiltered ? <Icons.FilterFilled /> : <Icons.FilterOutlined />
              }
              style={{ paddingRight: isFiltered ? 4 : 15 }}
              title={
                isFiltered
                  ? "You are viewing a filtered list"
                  : entityMetadata?.data?.config?.search?.textMessages
                    ?.buttonText || "Search & Filter"
              }
              onClick={() => switchFilterForm(true)}
            >
              Search
              {isFiltered && (
                <>
                  <Button
                    type="text"
                    danger
                    title={"Clear Filters"}
                    size="small"
                    style={{ marginLeft: 5 }}
                    onClick={(e) => {
                      clearFilters();
                      e.stopPropagation();
                    }}
                    icon={<Icons.CloseOutlined />}
                  ></Button>
                </>
              )}
            </Button>
          </>
        ),
        extra: (
          <Space>
            <CanAccess resource={RESOURCE_PATH.BACKGROUND_SCREENING} action="create" params={{ id: "1", dataProviderName: DATAPROVIDER_CREATE }}>
              <CreateButton icon={<Icons.PlusOutlined />}>Add</CreateButton>
            </CanAccess>
            {metaConfig?.export?.enabled &&
              <CanAccess key={RESOURCE_PATH.BACKGROUND_SCREENING} resource={RESOURCE_PATH.BACKGROUND_SCREENING} action="list" params={{ dataProviderName: DATAPROVIDER_EXPORT }}>
                <ExportButton onClick={triggerExport} loading={exportLoading} />
              </CanAccess>
            }
            {RenderWorkflowModal(entityStates)}
          </Space>
        ),
      }}
    >
      <Table {...tableProps}
        rowKey="id"
        pagination={{
          ...tableProps.pagination,
          position: ["bottomRight"],
          showTotal: (total => <Typography.Title level={4} style={{ marginRight: 10 }} >Total {total}</Typography.Title>)
        }}
      >
        <Table.Column
          title="Associate Name"
          dataIndex="name"
          key="name"
          render={(value) => <Typography.Text>{value}</Typography.Text>}
          onCell={rowClick}
          className="mouseHand"
          sorter
        />

        <Table.Column
          title="TCS Manager"
          dataIndex="managerUserId"
          key="managerUserId"
          render={(value) => <ShowUserCard id={value} />}
          onCell={rowClick}
          className="mouseHand"
          sorter
        />

        <Table.Column
          title="Country"
          dataIndex="countryId"
          key="countryId"
          render={(value) => (
            <LookupButton
              id={value}
              resource={RESOURCE_PATH.COUNTRY}
              noClick
            />
          )}
          onCell={rowClick}
          className="mouseHand"
          sorter
        />

        <Table.Column
          title="Screening Id"
          dataIndex="screeningId"
          key="screeningId"
          render={(value) => (
            <Typography.Text copyable={doesExist(value)}>{value}</Typography.Text>
          )}
          onCell={rowClick}
          className="mouseHand"
          sorter
        />

        <Table.Column
          dataIndex={["stateManager", "state"]}
          key="stateManager.state"
          title="Status"
          onCell={rowClick}
          className="mouseHand"
          sorter
          render={(value) => {
            if (value) {
              const currentStateMetadata = entityMetadata?.data?.states?.find((state) => state.state === value);
              return (<Tag color={currentStateMetadata?.color ?? "default"}>
                {currentStateMetadata?.displayName ?? value ?? "Loading"}
              </Tag>);
            }
          }}
        />
        <Table.Column
          dataIndex={["stateManager", "assignedTo"]}
          key="stateManager.assignedTo"
          title="Assigned To"
          onCell={rowClick}
          className="mouseHand"
          render={(value: string[]) =>
          (value?.length ? value.map((v, i) => (
            <div key={i} style={{ marginBottom: 5 }}>
              <ShowUserCard id={v} />
            </div>
          )) : "None")
          }
        />

        <Table.Column
          dataIndex="updatedAt"
          key="updatedAt"
          title="Last Updated"
          render={displayRelativeDate}
          defaultSortOrder={getDefaultSortOrder("updatedAt", sorter)}
          sorter
          onCell={rowClick}
          className="mouseHand"
        />
      </Table>
      <Drawer
        title={
          entityMetadata?.data?.config?.search?.textMessages
            ?.drawerFormHeader || "Search & Filter"
        }
        placement="right"
        size="default"
        open={filterOpen}
        onClose={() => {
          switchFilterForm(false);
        }}
      >
        {entityMetadata?.data && (
          <BackgroundScreeningFilter
            formProps={searchFormProps}
            onApplyFilter={() => {
              switchFilterForm(false);
              searchFormProps.form?.submit();
            }}
          />
        )}
      </Drawer>
    </List>
  );
};
